import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popconfirm, Input, Space } from 'antd';
import Button from '@iso/components/uielements/button';
import { Link, useRouteMatch } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import notification from '@iso/components/Notification';
import HelperText from '@iso/components/utility/helper-text';
import LayoutWrapper from '@iso/components/utility/layoutWrapper';
import PageTitleBar from '@iso/components/utility/PageTitleBar';
import PageTitleActions from '@iso/containers/Dashboard/PageTitleActions';
import FormWindow from '@iso/containers/Dashboard/FormWindow';
import EditSegment from './EditSegment';
import segmentActions from '@iso/redux/segment/actions';
import CardWrapper, { Box, StatusTag } from './Segment.style';
import Scrollbars from '@iso/components/utility/customScrollBar';
import TableWrapper from '../../Tables/AntTables/AntTables.styles';
import { b64_to_utf8 } from '@iso/lib/helpers/utility';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import Loader from '@iso/components/utility/loader';

const { initData, deleteSegment } = segmentActions;

export default function Segments() {
	const ses = sessionStorage.getItem("permissions");
	const arraySplit = b64_to_utf8(ses).split(",");
  const [selected, setSelected] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const { initialSegment, segments } = useSelector((state) => state.Segments);
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const searchInput = useRef(null);

  React.useEffect(() => {
    if (!initialSegment) {
      dispatch(initData());
    }
  }, [initialSegment]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex, collum) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, collum)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, collum)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Limpar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Fechar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        title={"Pesquisar pelo "+dataIndex+" do Segmento"}
        style={{
          color: '#000000',
        }}
      />
    ),
    onFilter: (value, record) =>
      record[collum].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === collum ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      width: '75%',
      render: (text) => <span>{text}</span>,
      ...getColumnSearchProps('Nome', 'name')
    },
    {
      title: 'Status',
      dataIndex: 'situation',
      key: 'situation',
      width: '25%',
      render: (text) => {
        let className;
        if (text === 'ativo' || text === 'Ativo' || text === 'ATIVO') {
          className = 'ativo';
        } else if ( text === 'inativo' || text === 'Inativo' || text === 'INATIVO' ) {
          className = 'inativo';
        }
        return <StatusTag className={className}>{text}</StatusTag>;
      },
    },
    {
      title: '',
      dataIndex: 'view',
      rowKey: 'view',
      width: '10%',
      render: (_, segment) =>{
        return (
          <div className="isoSegmentBtnView">
            {arraySplit.includes('Sub_Grupo_editar') || arraySplit.includes('all')  ? 
              <a href={`${match.path}/${segment._id}`} onClick={(event) => { event.preventDefault(); setEditingId(segment._id); }}>
                <Button color="primary" className="segmentViewBtn">
                  Editar
                </Button>
              </a>
            : ''}
            {arraySplit.includes('Sub_Grupo_excluir') || arraySplit.includes('all')  ? 
              <Popconfirm title="Deseja realmente deletar?" onConfirm={() => {
                const index = segments.map(inv => inv._id).indexOf(segment._id);
                dispatch(deleteSegment(segments[index]._id));
                notification('success', '1 segmento deletado');
              }}>
                <Button className="segmentDltBtn" onClick={() => { }}>
                  <i className="ion-android-delete" />
                </Button>
              </Popconfirm>
            : ''}
          </div>
        )
      }
    },
  ];

  const rowSelection = {
    hideDefaultSelections: true,
    selectedRowKeys: selected,
    onChange: (selected) => setSelected(selected),
    selections: [
      {
        rowKey: 'all-data',
        text: 'Selecionar todos',
        onSelect: () => setSelected(segments.map((segment) => segment._id)),
      },
      {
        rowKey: 'no-data',
        text: 'Desselecionar',
        onSelect: () => setSelected([]),
      }
    ],
    onSelection: (selected) => setSelected(selected),
  };

  return (
    <LayoutWrapper>
      
      <PageTitleBar title="Subgrupos" />
      <PageTitleActions primary={{ key: 'create', label: "Adicionar", onClick: () => setEditingId('0') }} />
      <Box style={{ border: 'none', boxShadow: 'none' }}>
        {!initialSegment ? (
          <Loader />
        ) : (
          <CardWrapper>
            {segments !== undefined && segments.length === 0 ? (
              <HelperText text="Sem Subgrupo" />
            ) : (
              <div className="isoSegmentTable">
                <div
                  style={{ width: '100%' }}
                >
                  <TableWrapper
                    rowKey={"_id"}
                    rowSelection={rowSelection}
                    dataSource={segments}
                    columns={columns}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                    className="segmentListTable"
                  />
                </div>
              </div>
            )}
          </CardWrapper>
        )}
      </Box>
          <FormWindow
        open={editingId !== null}
        title={editingId === '0' ? 'Cadastro de Subgrupo' : 'Edição de Subgrupo'}
        onClose={() => setEditingId(null)}
      >
        {({ footerEl }) =>
          editingId !== null ? (
            <EditSegment
              segmentId={editingId}
              inModal
              footerEl={footerEl}
              onCancel={() => setEditingId(null)}
              onSaved={() => {
                setEditingId(null);
                dispatch(initData());
              }}
            />
          ) : null
        }
      </FormWindow>
    </LayoutWrapper>
  );
}
